import LeaseApiService from './apiService';
import { endPoint } from '../EndPoints/endPoint';

/**
 * @typedef {{ value: string, text: string }} DropdownItem
 * @typedef {{ customerNo: string, accountCode: string, customerName: string, address1: string, address2: string, contactNo: string }} CustomerDetail
 * @typedef {{ receiptNo: string, voucherNo: string, voucherDate: string, invoiceNo: string, invoiceAmount: number, vat: number, st: number,
 *   totalAmount: number, amountPaid: number, balance: number, financialYear: string, currency: string, convertedAmount: number }} Invoice
 * @typedef {{ accountCode: string, accountName: string, balance: number }} DebitAccount
 * @typedef {{ voucherNo: string, voucherDate: string, refNo: string, chequeNo: string, chequeDate: string, balance: number,
 *   currency: string, conversionRate: number }} SetOffVoucher
 * @typedef {{ rdNo: number, voucherNo: string, financialYear: string, voucherDate: string, paymentType: string, customerNo: string,
 *   customerName: string, amount: number, totalRecords: number }} ReceiptRow
 * @typedef {{ receiptNo: string, amount: number }} Allocation
 * @typedef {{ customerNo: string, paymentType: string, voucherDate: string, debitAccountCode?: string, currency?: string,
 *   conversionRate?: number, chequeNo?: string|null, chequeDate?: string|null, drawnBank?: string|null, amount: number,
 *   refNo: string|null, commonNarration: string|null, costCenter1: string|null, costCenter2: string|null, costCenter3: string|null,
 *   costCenter4: string|null, additionalAccountCode: string|null, additionalAmount: number, setOffVoucherNo?: string,
 *   setOffFinancialYear?: string, allocations: Allocation[] }} CreateReceiptRequest
 * @typedef {{ paymentType: string, debitAccountCode: string, voucherDate: string, chequeNo: string|null, chequeDate: string|null,
 *   drawnBank: string|null, refNo: string|null, commonNarration: string|null, costCenter1: string|null, costCenter2: string|null,
 *   costCenter3: string|null, costCenter4: string|null }} UpdateReceiptRequest
 * @typedef {{ voucherNo: string, rdNo: number, financialYear: string }} SaveResult
 * @typedef {{ ok: boolean, status: number, message: string, result: any, dropdowns: any, pagination: any }} ApiResult
 */

const api = new LeaseApiService();
const http = api.axiosInstance;

const GENERIC_ERROR = 'Something went wrong. Please try again.';

const errorMessage = (status, serverMessage) => {
    if (status === 400) return serverMessage || 'Invalid request.';
    if (status === 404) return 'Receipt not found.';
    return GENERIC_ERROR;
};

/** Normalises success and failure into one shape so callers never touch axios errors. */
const call = async (request) => {
    try {
        const res = await request();
        const body = res?.data;
        const status = body?.statusCode ?? res?.status;
        return {
            ok: status >= 200 && status < 300,
            status,
            message: body?.message || '',
            result: body?.result ?? body?.data ?? null,
            dropdowns: body?.dropdowns ?? null,
            pagination: body?.paginationData ?? null,
        };
    } catch (error) {
        const status = error?.response?.status || 0;
        return { ok: false, status, message: errorMessage(status, error?.response?.data?.message), result: null, dropdowns: null, pagination: null };
    }
};

const withQuery = (params) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && `${value}`.trim() !== '') q.append(key, value);
    });
    const s = q.toString();
    return s ? `?${s}` : '';
}; 

export const getDropdowns = (dropdownTypes) => call(() => http.post(endPoint.LGIR_DROPDOWNS, { dropdownTypes }));

/** partyType: 'Tenant' | 'Landlord' | 'Freehold'. Resolves to [{ customerNo, customerName }]. */
export const getCustomersByUnit = ({ partyType, propertyName, unitNo }) =>
    call(() => http.get(endPoint.LGIR_UNIT_PARTY + withQuery({ partyType, propertyName, unitNo })));

export const getCustomerDetail = (customerNo) => call(() => http.get(`${endPoint.LGIR_CUSTOMER}${encodeURIComponent(customerNo)}`));

export const getOutstandingInvoices = ({ customerNo, costCenter1, costCenter2 }) =>
    call(() => http.get(endPoint.LGIR_INVOICES + withQuery({ customerNo, costCenter1, costCenter2 })));

export const getDebitAccounts = ({ paymentType, financialYear }) =>
    call(() => http.get(endPoint.LGIR_DEBIT_ACCOUNTS + withQuery({ paymentType, financialYear })));

export const getSetOffVouchers = ({ customerNo, financialYear }) =>
    call(() => http.get(endPoint.LGIR_SETOFF_VOUCHERS + withQuery({ customerNo, financialYear })));

export const getReceipts = (params) => call(() => http.get(endPoint.LGIR_LIST + withQuery(params)));

export const getReceiptByRdNo = (rdNo) => call(() => http.get(`${endPoint.LGIR_BY_RDNO}${rdNo}`));

/** @param {CreateReceiptRequest} body */
export const createReceipt = (body) => call(() => http.post(endPoint.LGIR_CREATE, body));

/** @param {UpdateReceiptRequest} body */
export const updateReceipt = (rdNo, body) => call(() => http.put(`${endPoint.LGIR_UPDATE}${rdNo}`, body));

export const deleteSetOffAllocation = (rdNo) => call(() => http.delete(`${endPoint.LGIR_DELETE_SETOFF}${rdNo}`));

const leaseGeneralInvoiceReceiptApi = {
    getDropdowns, getCustomersByUnit, getCustomerDetail, getOutstandingInvoices, getDebitAccounts, getSetOffVouchers,
    getReceipts, getReceiptByRdNo, createReceipt, updateReceipt, deleteSetOffAllocation,
};

export default leaseGeneralInvoiceReceiptApi;
